import { useSelector } from 'react-redux';
import { type ArrayHelpers } from 'formik';
import { Box, Checkbox } from '@rsos/base-components';
import ChatAvatar from '@rsos/components/capstone/Chat/ChatAvatar';
import { Text } from '@rsos/components/capstone/base';
import { type AgencyConnection } from '@rsos/reduxDataStore/src/features/peerAgencies/peerAgencies.types';
import { AppState } from '../../../../types/AppState';
import { ShareTextAsButton } from '../../ShareEvent.styles';

interface ShareEventECCsProps {
  selectedAgencies: string[];
  peerAgencies: AgencyConnection[];
  arrayHelpers: ArrayHelpers;
  setFieldValue: (field: string, value: unknown) => void;
  onAgencyDisplayNameChange?: (names: string[]) => void;
}

const ShareEventECCs = ({
  selectedAgencies,
  peerAgencies,
  arrayHelpers,
  setFieldValue,
  onAgencyDisplayNameChange,
}: ShareEventECCsProps) => {
  const selectedCallerID = useSelector(
    (state: AppState) => state.emergencyCalls.selectedCallerID,
  );
  const sharedWithAgencies = useSelector(
    (state: AppState) =>
      (selectedCallerID &&
        state.emergencyCalls.callQueue[selectedCallerID]?.agencyShareChat
          ?.sharedWithAgencies) ||
      [],
  );

  /**
   * set checkbox disabled if an event has already been shared or if an agency is offline
   */
  const handleDisabled = (
    agencyAccountID: string,
    agencyActiveStatus: boolean,
  ) => {
    return sharedWithAgencies.includes(agencyAccountID) || !agencyActiveStatus;
  };

  /**
   * Get display names from selected agencies
   */
  const getDisplayNamesFromSelected = (agencyIds: string[]) => {
    return peerAgencies
      .filter(agency => agencyIds.includes(agency.link_to.account_id))
      .map(agency => agency.link_to.display_name);
  };

  /**
   * handle checkbox value and bind to a single array
   */
  const handleCheckbox = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    const isSelected = event.target.checked && !event.target.disabled;

    if (isSelected) {
      arrayHelpers.push(value);
    } else {
      const indexToRemove = selectedAgencies.indexOf(value);
      if (indexToRemove !== -1) {
        arrayHelpers.remove(indexToRemove);
      }
    }

    const newSelectedAgencies = isSelected
      ? [...selectedAgencies, value]
      : selectedAgencies.filter(id => id !== value);

    const newDisplayNames = getDisplayNamesFromSelected(newSelectedAgencies);
    onAgencyDisplayNameChange?.(newDisplayNames);
  };

  const handleSelectAll = () => {
    const agenciesToAdd = peerAgencies.filter(agency => {
      const agencyAccountId = agency.link_to.account_id;
      return (
        !selectedAgencies.includes(agencyAccountId) &&
        !sharedWithAgencies.includes(agencyAccountId) &&
        agency.link_to.active
      );
    });

    agenciesToAdd.forEach(agency => {
      arrayHelpers.push(agency.link_to.account_id);
    });

    // Update display names
    const newSelectedAgencies = [
      ...selectedAgencies,
      ...agenciesToAdd.map(a => a.link_to.account_id),
    ];
    const newDisplayNames = getDisplayNamesFromSelected(newSelectedAgencies);
    onAgencyDisplayNameChange?.(newDisplayNames);
  };

  /**
   * reset the form to its initial state
   */
  const handleClearAll = () => {
    setFieldValue('selectedAgencies', []);
    onAgencyDisplayNameChange?.([]);
  };

  return (
    <Box>
      <Text color="secondaryText" size="normal">
        Select contact(s) to share
      </Text>

      <Box horizontal marginY="16px">
        <ShareTextAsButton mr="10px" onClick={handleSelectAll}>
          Select All
        </ShareTextAsButton>
        <ShareTextAsButton onClick={handleClearAll}>
          Clear All
        </ShareTextAsButton>
      </Box>

      {peerAgencies.map(item => {
        const agencyAccountId = item.link_to.account_id;
        const agencyDisplayName = item.link_to.display_name;
        const isDisabled = handleDisabled(agencyAccountId, item.link_to.active);
        return (
          <Box horizontal mb="8px" key={agencyAccountId} align="center">
            <Checkbox
              onChange={event => handleCheckbox(event, agencyAccountId)}
              isChecked={selectedAgencies.includes(agencyAccountId)}
              disabled={isDisabled}
              label={agencyDisplayName}
            />
            <ChatAvatar
              displayActiveIndicator
              isActive={item.link_to.active}
              name={agencyDisplayName}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default ShareEventECCs;
